import { useEffect, useMemo, useState } from 'react'
import type { PmmRepository } from '../data/repository'
import type { FoundationCapacity, FoundationItemView, FoundationView } from '../domain/foundation'
import { CAPACITY_MODES } from '../domain/capacityMode'
import '../foundation.css'

interface FoundationWorkspaceProps { repository: PmmRepository; capacity: FoundationCapacity }
interface FoundationTodayCardProps { repository: PmmRepository; capacity: FoundationCapacity; onOpen: () => void }

const kindLabels = {
  recognition_story: 'Recognition story',
  embodied_practice: 'Practical exercise',
  life_application: 'Life application',
} as const

function mutationId(itemId: string, action: string): string {
  return `foundation-${action}-${itemId}-${crypto.randomUUID()}`
}

function paragraphs(value: string) {
  return value.split(/\n\s*\n/).filter(Boolean).map((paragraph, index) => <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>)
}

function progressLabel(view: FoundationView): string {
  if (view.progress.completedItems === view.progress.totalItems) return 'Foundation completed'
  if (view.progress.currentWeekNumber < view.progress.targetWeekNumber) return 'Your place is protected - continue at your pace'
  if (view.progress.currentWeekNumber > view.progress.targetWeekNumber) return 'You have moved ahead of the suggested pace'
  return 'At the suggested pace'
}

export function FoundationTodayCard({ repository, capacity, onOpen }: FoundationTodayCardProps) {
  const [view, setView] = useState<FoundationView | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    repository.getFoundationJourney(capacity).then((next) => {
      if (active) setView(next)
    }).catch((cause) => {
      if (active) setError(cause instanceof Error ? cause.message : 'The Foundation could not be loaded.')
    })
    return () => { active = false }
  }, [capacity, repository])

  if (error) return <section className="foundation-today is-error"><p className="eyebrow">16-Week Foundation</p><h2>Your place is protected.</h2><p>{error}</p></section>
  if (!view) return <section className="foundation-today is-loading"><p className="eyebrow">16-Week Foundation</p><h2>Preparing your next shared session...</h2></section>
  if (view.availability === 'onboarding_required') return null
  if (view.delivery.mode === 'paused') return <section className="foundation-today is-paused"><p className="eyebrow">Today’s pace</p><h2>{view.delivery.label}</h2><p>{view.delivery.explanation}</p><button type="button" className="foundation-secondary" onClick={onOpen}>See what this means</button></section>

  const next = view.recommendedItem
  return <section className="foundation-today" aria-labelledby="foundation-today-title">
    <div className="foundation-today-copy">
      <p className="eyebrow">Your shared Foundation · Week {view.progress.currentWeekNumber} of 16</p>
      <h2 id="foundation-today-title">{next ? next.title : 'You completed the 16-Week Foundation.'}</h2>
      <p>{next ? next.subtitle : 'Your living code remains available for review. Completion is not a claim that growth is finished.'}</p>
      <div className="foundation-today-meta"><span>{progressLabel(view)}</span>{next && <span>{next.minutes} min · {view.delivery.mode === 'lighter' ? 'lighter path ready' : kindLabels[next.kind]}</span>}</div>
    </div>
    <button type="button" className="foundation-primary" onClick={onOpen}>{next ? 'Open this session' : 'Review the journey'}</button>
  </section>
}

export function FoundationWorkspace({ repository, capacity }: FoundationWorkspaceProps) {
  const [view, setView] = useState<FoundationView | null>(null)
  const [activeItemId, setActiveItemId] = useState('')
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [share, setShare] = useState<Record<string, boolean>>({})
  const [lighter, setLighter] = useState<Record<string, boolean>>({})
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(true)
  const [viewMode, setViewMode] = useState<'current' | 'pending' | 'journey' | 'completed'>('current')

  useEffect(() => {
    let active = true
    setBusy(true)
    repository.getFoundationJourney(capacity).then((next) => {
      if (!active) return
      setView(next)
      setActiveItemId((current) => next.weeks.flatMap((week) => week.items).some((item) => item.itemId === current && item.status !== 'locked')
        ? current
        : next.recommendedItem?.itemId ?? next.currentWeek?.items.find((item) => item.status !== 'locked')?.itemId ?? '')
      setBusy(false)
    }).catch((cause) => {
      if (!active) return
      setNotice(cause instanceof Error ? cause.message : 'The Foundation could not be prepared safely.')
      setBusy(false)
    })
    return () => { active = false }
  }, [capacity, repository])

  const allItems = useMemo(() => view?.weeks.flatMap((week) => week.items) ?? [], [view])
  const activeItem = allItems.find((item) => item.itemId === activeItemId)
    ?? view?.recommendedItem
    ?? view?.currentWeek?.items[0]
    ?? null

  async function record(item: FoundationItemView, action: 'completed' | 'deferred') {
    setBusy(true)
    try {
      const result = await repository.recordFoundationInteraction({
        itemId: item.itemId,
        action,
        selectedOption: selectedOptions[item.itemId],
        note: notes[item.itemId],
        allowForPersonalisation: share[item.itemId] === true,
        usedLighterPath: lighter[item.itemId] === true || resultModeIsLighter(),
        capacity,
        clientMutationId: mutationId(item.itemId, action),
      })
      setView(result.view)
      if (action === 'completed') {
        const next = result.view.recommendedItem
        setActiveItemId(item.itemId)
        setNotice(next
          ? `Session complete. ${next.title} is ready whenever you have more time - today, later this week or after a break.`
          : 'Foundation complete. Your living code and every completed session remain available.')
      } else {
        setNotice('Kept pending without judgment. It will remain available until you choose to return.')
      }
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That Foundation update could not be saved safely.')
    } finally { setBusy(false) }
  }

  function resultModeIsLighter() {
    return view?.delivery.mode === 'lighter'
  }

  function openItem(item: FoundationItemView) {
    if (item.status === 'locked' || view?.delivery.mode === 'paused') return
    setActiveItemId(item.itemId)
    setViewMode('current')
    document.getElementById('foundation-session')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (busy && !view) return <section className="foundation-workspace"><div className="locked-review"><span>Preparing your Foundation</span><h3>Loading the shared sequence and protecting your exact place...</h3></div></section>

  if (!view) return <section className="foundation-workspace"><div className="foundation-empty"><h2>Your place is protected.</h2><p>{notice || 'The Foundation is temporarily unavailable.'}</p></div></section>

  const visibleQueue = viewMode === 'pending'
    ? view.pendingItems
    : viewMode === 'completed'
      ? view.recentlyCompletedItems
      : view.currentWeek?.items ?? []

  const activeMode = view.delivery.mode === 'lighter' ? CAPACITY_MODES.low : CAPACITY_MODES[capacity]

  return <main className="foundation-workspace">
    <header className="foundation-hero">
      <div>
        <p className="eyebrow">The shared journey · self-paced and never scored</p>
        <h1>The Primal<br /><em>Foundation.</em></h1>
        <p>{view.programme.description}</p>
      </div>
      <div className="foundation-progress-card" aria-label="Foundation progress">
        <span>Current Foundation week</span><strong>{view.progress.currentWeekNumber}<small> / 16</small></strong>
        <div className="foundation-progress-track"><i style={{ width: `${Math.round(view.progress.completedItems / view.progress.totalItems * 100)}%` }} /></div>
        <p>{view.progress.completedItems} of {view.progress.totalItems} sessions complete</p>
        <small>{progressLabel(view)}</small>
      </div>
    </header>

    <section className={`foundation-delivery mode-${view.delivery.mode}`} aria-label="Today’s mode">
      <span className="foundation-delivery-mark" aria-hidden="true">{view.delivery.mode === 'paused' ? '!' : activeMode.mark}</span>
      <div>
        <p className="eyebrow">Today’s mode</p>
        <h2>{view.delivery.mode === 'paused' ? view.delivery.label : activeMode.name}</h2>
        {view.delivery.mode === 'paused'
          ? <p>{view.delivery.explanation}</p>
          : <><blockquote>“{activeMode.quote}”</blockquote><p>{activeMode.acknowledgement}</p></>}
      </div>
    </section>

    {view.availability === 'onboarding_required' && <section className="foundation-empty"><p className="eyebrow">Foundation locked</p><h2>Complete onboarding first.</h2><p>The Foundation begins after your baseline is submitted. It does not require personal AI guidance to be published.</p></section>}

    {view.availability === 'ready' && <>
      <nav className="foundation-view-tabs" aria-label="Foundation views">
        <button type="button" className={viewMode === 'current' ? 'is-active' : ''} onClick={() => setViewMode('current')}>This Foundation week <span>{view.currentWeek?.completedCount ?? 0}/{view.currentWeek?.requiredCount ?? 0}</span></button>
        <button type="button" className={viewMode === 'pending' ? 'is-active' : ''} onClick={() => setViewMode('pending')}>Pending <span>{view.pendingItems.length}</span></button>
        <button type="button" className={viewMode === 'journey' ? 'is-active' : ''} onClick={() => setViewMode('journey')}>All 16 weeks</button>
        <button type="button" className={viewMode === 'completed' ? 'is-active' : ''} onClick={() => setViewMode('completed')}>Completed</button>
      </nav>

      {viewMode === 'journey' ? <section className="foundation-roadmap" aria-label="16-week Foundation roadmap">
        {view.weeks.map((week) => <article key={week.weekNumber} className={`foundation-week-card state-${week.state}`}>
          <span>Week {String(week.weekNumber).padStart(2, '0')} · {week.phase}</span>
          <h3>{week.title}</h3><p>{week.promise}</p>
          <footer><strong>{week.completedCount}/{week.requiredCount}</strong><small>{week.state === 'locked' ? 'Unlocks after the current week' : week.state}</small></footer>
        </article>)}
      </section> : <section className="foundation-queue" aria-label={`${viewMode} Foundation sessions`}>
        <div className="foundation-queue-heading"><div><p className="eyebrow">{viewMode === 'current' ? `Week ${view.currentWeek?.weekNumber} · ${view.currentWeek?.phase}` : viewMode}</p><h2>{viewMode === 'current' ? view.currentWeek?.title : viewMode === 'pending' ? 'Nothing disappears at week change.' : 'Work you have already completed.'}</h2></div>{viewMode === 'current' && <p>{view.currentWeek?.overview}</p>}</div>
        {visibleQueue.length ? <div className="foundation-session-list" role="tablist" aria-label="Choose this week’s Foundation session">{visibleQueue.map((item) => <button type="button" role="tab" id={`foundation-lesson-tab-${item.itemId}`} aria-controls="foundation-session" aria-selected={activeItem?.itemId === item.itemId} key={item.itemId} className={`${activeItem?.itemId === item.itemId ? 'is-active' : ''} status-${item.status}`} onClick={() => openItem(item)}><span>{kindLabels[item.kind]} · {item.minutes} min</span><strong>{item.title}</strong><small>{item.status === 'deferred' ? 'Pending - your place is protected' : item.status === 'completed' ? 'Completed' : item.subtitle}</small></button>)}</div> : <div className="foundation-empty"><h3>{viewMode === 'pending' ? 'Nothing is pending.' : 'No completed sessions in this view yet.'}</h3><p>There is no penalty, score or lost work here.</p></div>}
      </section>}

      {view.delivery.mode === 'paused' ? <section className="foundation-paused-detail"><p className="eyebrow">Immediate safety first</p><h2>Your Foundation place will not move or expire.</h2><p>Deep reflection, relationship confrontation and progress pressure are paused. Use immediate local emergency, crisis or trusted human support when safety cannot be maintained. The app is not a crisis service.</p></section> : activeItem && viewMode !== 'journey' && <article className="foundation-session" id="foundation-session" role="tabpanel" aria-labelledby={`foundation-lesson-tab-${activeItem.itemId}`}>
        <header><div><p className="eyebrow">Week {activeItem.weekNumber} · {kindLabels[activeItem.kind]}</p><h2>{activeItem.title}</h2><p>{activeItem.subtitle}</p></div><div className="foundation-time"><strong>{activeItem.minutes}</strong><span>minutes</span></div></header>

        {activeItem.story && <section className="foundation-story"><p className="eyebrow">Read slowly · a composite recognition story</p>{paragraphs(activeItem.story)}<aside>This story is here to help you recognise a possibility, not to diagnose your cause or give you an answer to copy.</aside></section>}

        <section className="foundation-teaching"><div><p className="eyebrow">What this means</p><h3>Plain language before reflection</h3></div><ul>{activeItem.teaching.map((line) => <li key={line}>{line}</li>)}</ul></section>

        <section className="foundation-signs"><p className="eyebrow">How this can show up in real life</p><div>{activeItem.howItShowsUp.map((line) => <span key={line}>{line}</span>)}</div></section>

        <section className="foundation-examples"><p className="eyebrow">Examples · for recognition, not answers to copy</p>{activeItem.examples.map((line) => <blockquote key={line}>{line}</blockquote>)}</section>

        <section className={`foundation-steps ${lighter[activeItem.itemId] || view.delivery.mode === 'lighter' ? 'show-lighter' : ''}`}>
          <div className="foundation-step-heading"><div><p className="eyebrow">Choose the version that fits today</p><h3>{lighter[activeItem.itemId] || view.delivery.mode === 'lighter' ? 'The lighter path' : 'The full practice'}</h3></div><button type="button" className="foundation-secondary" onClick={() => setLighter((current) => ({ ...current, [activeItem.itemId]: !(current[activeItem.itemId] === true) }))}>{lighter[activeItem.itemId] ? 'Show full practice' : 'Use the lighter path'}</button></div>
          <ol>{(lighter[activeItem.itemId] || view.delivery.mode === 'lighter' ? activeItem.lighterPath : activeItem.steps).map((step, index) => <li key={step}><span>{String(index + 1).padStart(2, '0')}</span><p>{step}</p></li>)}</ol>
        </section>

        {activeItem.responsePrompt && <section className="foundation-response"><p className="eyebrow">Optional response</p><h3>{activeItem.responsePrompt}</h3><p>Choose what is true. A private answer, uncertainty or no answer is more useful than performing the response you think a man should give.</p><div className="foundation-response-options">{activeItem.responseOptions?.map((option) => <button type="button" key={option.value} className={selectedOptions[activeItem.itemId] === option.value ? 'is-active' : ''} onClick={() => setSelectedOptions((current) => ({ ...current, [activeItem.itemId]: option.value }))}>{option.label}</button>)}</div><label className="foundation-note"><span>Optional private note</span><textarea maxLength={1500} value={notes[activeItem.itemId] ?? ''} onChange={(event) => setNotes((current) => ({ ...current, [activeItem.itemId]: event.target.value }))} placeholder="A sentence is enough. Leaving this blank is also meaningful." /></label><label className="foundation-share"><input type="checkbox" checked={share[activeItem.itemId] === true} onChange={(event) => setShare((current) => ({ ...current, [activeItem.itemId]: event.target.checked }))} /><span><strong>Allow this answer to inform me through personalisation and better understanding of the man you are. This will be kept private.</strong>Your answer may inform later Coach-reviewed personal guidance. It never changes or scores the Foundation sequence.</span></label></section>}

        <p className="foundation-completion"><strong>Completion means:</strong> {activeItem.completionSignal}</p>
        <footer className="foundation-actions"><button type="button" className="foundation-secondary" disabled={busy || activeItem.status === 'completed'} onClick={() => void record(activeItem, 'deferred')}>Keep pending</button><button type="button" className="foundation-primary" disabled={busy || activeItem.status === 'completed'} onClick={() => void record(activeItem, 'completed')}>{activeItem.status === 'completed' ? 'Session completed' : 'Complete this session'}</button></footer>
        {view.recommendedItem && activeItem.status === 'completed' && <div className="foundation-more-time"><div><p className="eyebrow">Have more time today?</p><h3>Your next session is available now.</h3><p>You can continue, stop here, return tomorrow or let this week take longer. Nothing expires.</p></div><button type="button" className="foundation-primary" onClick={() => openItem(view.recommendedItem!)}>Yes, continue to {view.recommendedItem.title}</button></div>}
        {activeItem.sourceNotes?.length ? <details className="foundation-sources"><summary>Sources and influences</summary>{activeItem.sourceNotes.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.label}</a>)}</details> : null}
      </article>}
    </>}

    {notice && <div className="toast" role="status"><span />{notice}<button type="button" onClick={() => setNotice('')} aria-label="Dismiss message">×</button></div>}
  </main>
}
